import {
  DEFAULT_INSPECTION_MINUTES,
  TIME_SLOTS,
  assigneePatch,
  inspectionInvitees,
  type Assignee,
} from "@roof-hawk/core";

import { t } from "../i18n";
import { useSession } from "../session";
import { AssigneePicker } from "./AssigneePicker";

/**
 * The inspection form: when, how long, who is going, and notes.
 *
 * Two screens draw it — the checklist's Schedule inspection box, and the
 * bottom of New Lead (Jonathon, 2026-09-28: "i want to be able to schedule an
 * inspection on the new lead pop up screen towards the bottom of the screen").
 * One component so the two can never offer different people or different
 * times.
 */
export interface InspectionDraft {
  day: string;
  /** A quarter-hour slot from TIME_SLOTS, as "HH:MM". */
  slot: string;
  durationMinutes: number;
  one: Assignee | null;
  two: Assignee | null;
  notes: string;
}

export const emptyInspectionDraft = (): InspectionDraft => ({
  day: "",
  slot: "",
  durationMinutes: DEFAULT_INSPECTION_MINUTES,
  one: null,
  two: null,
  notes: "",
});

/** When it is, as the ISO timestamp the job stores — or null without both. */
export const inspectionIso = (draft: InspectionDraft): string | null =>
  draft.day && draft.slot
    ? new Date(`${draft.day}T${draft.slot}`).toISOString()
    : null;

/** The four slot columns, one of each pair always null. */
export const inspectionAssigneeColumns = (draft: InspectionDraft) => ({
  ...assigneePatch(1, draft.one),
  ...assigneePatch(2, draft.two),
});

export function InspectionFields({
  draft,
  options,
  onChange,
}: {
  draft: InspectionDraft;
  options: readonly Assignee[];
  onChange: (next: InspectionDraft) => void;
}) {
  const { people, crews } = useSession();
  const set = (patch: Partial<InspectionDraft>) =>
    onChange({ ...draft, ...patch });
  const { one, two } = draft;

  /*
   * Everybody going, and how to invite them.
   *
   * `assigneeOptions` deliberately carries only what a picker needs, so the
   * address is looked up here against the two lists it was built from. A crew
   * without one is not an error — plenty are a phone number and a name — and
   * `inspectionInvitees` reports them, so the hint below can say who was left
   * off rather than letting somebody find out by not turning up.
   */
  const going = [one, two].filter((a): a is Assignee => a !== null);
  const emailOf = (assignee: Assignee): string | null =>
    assignee.kind === "staff"
      ? (people.find((p) => p.id === assignee.id)?.email ?? null)
      : (crews.find((c) => c.id === assignee.id)?.email ?? null);
  const { uninvited } = inspectionInvitees(
    going.map((a) => ({ ...a, email: emailOf(a) })),
  );

  return (
    <>
      <label className="na-hint">{t("Date")}</label>
      <input
        type="date"
        aria-label={t("Inspection date")}
        value={draft.day}
        onChange={(e) => set({ day: e.target.value })}
      />
      <label className="na-hint">{t("Time")}</label>
      <select
        aria-label={t("Inspection time")}
        value={draft.slot}
        onChange={(e) => set({ slot: e.target.value })}
      >
        <option value="">—</option>
        {TIME_SLOTS.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      {/* Back, because the real Google sync (ADR 0040) is the thing that has
          somewhere to put it: it is the end time on the calendar entry. Still
          not a column — an inspection that runs long does not need the database
          to know — so it is 60 again next time this is opened. */}
      <label className="na-hint">{t("How long")}</label>
      <select
        aria-label={t("How long")}
        value={draft.durationMinutes}
        onChange={(e) => set({ durationMinutes: Number(e.target.value) })}
      >
        {[30, 60, 90, 120].map((m) => (
          <option key={m} value={m}>
            {t("{n} min", { n: m })}
          </option>
        ))}
      </select>
      {uninvited.length > 0 && (
        <div className="na-hint" style={{ marginTop: 6 }}>
          {t(
            "No email address for {names}, so they will not get the invitation — ring them, or add an address under Tools.",
            { names: uninvited.join(` ${t("or")} `) },
          )}
        </div>
      )}
      <label className="na-hint">{t("Who is going")}</label>
      <AssigneePicker
        value={one}
        options={options.filter(
          (o) => !(two && o.kind === two.kind && o.id === two.id),
        )}
        placeholder={t("Pick an inspector or crew…")}
        onChange={(picked) => set({ one: picked })}
      />
      <label className="na-hint">{t("Second person or crew (optional)")}</label>
      <AssigneePicker
        value={two}
        options={options.filter(
          (o) => !(one && o.kind === one.kind && o.id === one.id),
        )}
        placeholder={t("Anyone else going…")}
        onChange={(picked) => set({ two: picked })}
      />
      <label className="na-hint">
        {t("Notes (paste anything the inspector should see)")}
      </label>
      <textarea
        style={{ minHeight: 90 }}
        value={draft.notes}
        onChange={(e) => set({ notes: e.target.value })}
      />
    </>
  );
}

/**
 * The form folded behind a button, for a screen where booking is optional —
 * New Lead, where most leads are booked later. Null while folded.
 */
export function OptionalInspection({
  draft,
  options,
  onChange,
}: {
  draft: InspectionDraft | null;
  options: readonly Assignee[];
  onChange: (next: InspectionDraft | null) => void;
}) {
  return (
    <div className="fgrid one" style={{ marginTop: 10 }}>
      <div className="field">
        {draft ? (
          <>
            <label>
              {t("Inspection")}{" "}
              <span className="chg" onClick={() => onChange(null)}>
                {t("Remove")}
              </span>
            </label>
            <InspectionFields
              draft={draft}
              options={options}
              onChange={onChange}
            />
          </>
        ) : (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onChange(emptyInspectionDraft())}
          >
            {t("+ Schedule inspection")}
          </button>
        )}
      </div>
    </div>
  );
}
